// frontend/src/errors/ErrorBoundary.tsx - PHASE 1 Enhanced with Backend Logging
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button, Container } from '@mui/material';
import {
  Error as ErrorIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { colors } from '../theme/theme';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

const ErrorContainer = styled(Container)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '400px',
  padding: '3rem',
  textAlign: 'center',
});

const ErrorIconStyled = styled(ErrorIcon)({
  fontSize: '5rem',
  color: colors.secondary.main,
  marginBottom: '1.5rem',
});

const ErrorTitle = styled(Typography)({
  fontSize: '2rem',
  fontWeight: 700,
  color: colors.tertiary.dark,
  marginBottom: '1rem',
});

const ErrorMessage = styled(Typography)({
  fontSize: '1.2rem',
  color: colors.tertiary.main,
  marginBottom: '2rem',
  maxWidth: '600px',
});

const ErrorDetails = styled(Box)({
  backgroundColor: colors.background.light,
  border: `2px solid ${colors.secondary.light}`,
  borderRadius: '8px',
  padding: '1rem',
  marginBottom: '2rem',
  maxWidth: '800px',
  width: '100%',
  textAlign: 'left',
  overflow: 'auto',
});

const ReloadButton = styled(Button)({
  fontSize: '1.2rem',
  fontWeight: 600,
  padding: '12px 32px',
  backgroundColor: colors.primary.main,
  color: '#fff',
  '&:hover': {
    backgroundColor: colors.primary.dark,
  },
});

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  /**
   * Send React component error to backend for logging - Phase 1
   * Fire-and-forget, non-blocking
   */
  private logErrorToBackend = (error: Error, errorInfo: ErrorInfo) => {
    try {
      const authUser = localStorage.getItem('authUser');
      const userId = authUser ? JSON.parse(authUser).id : null;

      fetch('/api/errorlogs/frontend', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: `React Error: ${error.message}`,
          stackTrace: errorInfo.componentStack || error.stack,
          url: window.location.href,
          statusCode: undefined,
          httpMethod: undefined,
          context: `Component Error - ${errorInfo.componentStack?.split('\n')[0]}`,
          userAgent: navigator.userAgent,
          userId: userId,
          timestamp: new Date().toISOString(),
        }),
      }).catch(() => {
        console.debug('Failed to log React error to backend');
      });
    } catch (err) {
      console.debug('Error logging failed:', err);
    }
  };

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });

    // Send React component error to backend for logging - Phase 1
    this.logErrorToBackend(error, errorInfo);
  }

  private handleReload = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <ErrorContainer>
          <ErrorIconStyled />
          <ErrorTitle>Something went wrong</ErrorTitle>
          <ErrorMessage>
            An unexpected error occurred. Please try reloading the page.
          </ErrorMessage>

          {process.env.NODE_ENV === 'development' && this.state.error && (
            <ErrorDetails>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ fontWeight: 700 }}
              >
                Error Details (Development Only):
              </Typography>
              <Typography
                variant="body2"
                component="pre"
                sx={{ fontSize: '0.9rem' }}
              >
                {this.state.error.toString()}
              </Typography>
              {this.state.errorInfo && (
                <Typography
                  variant="body2"
                  component="pre"
                  sx={{
                    fontSize: '0.85rem',
                    mt: 1,
                    color: colors.tertiary.light,
                  }}
                >
                  {this.state.errorInfo.componentStack}
                </Typography>
              )}
            </ErrorDetails>
          )}

          <ReloadButton
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={this.handleReload}
          >
            Reload Page
          </ReloadButton>
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
